import VdpPage, { PresentVdpPageProps } from 'components/VdpPage';
import {
  fetchAdInfo,
  fetchVehicleWithExperience,
  FetchVehicleWithExperienceParams,
} from 'lib/AutolistAPI';
import getIpAddress from 'lib/getIpAddress';
import {
  buildVdpPageProps,
  extractVehicleWithExperienceParamsFromQuery,
} from 'lib/hooks/useBuildVdpPageProps';
import i18next from 'lib/i18next';
import { inIframe } from 'lib/Utilities';
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useEffect } from 'react';
import XOR from 'lib/types/xor';
import Layout, { LayoutProps } from 'components/Layout';
import { useTranslation } from 'react-i18next';
import DirectToVdpContext from 'contexts/DirectToVdpContext';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import vehicleNotFoundAtom from 'atoms/VehicleNotFoundAtom';
import { AdInfo } from 'lib/CarGurusAds';
import VehicleWithExperience from 'types/Apis/VehicleWithExperience';
import { serverSideMaxMindGeolocate } from 'lib/location/MaxMind';
import { getIpMaxMindValue, setIpMaxMindValue } from 'lib/cache/Redis';
import InactiveListing from 'components/InactiveListing';

export type VdpOrNotFoundParams = {
  vin: string;
};

export function VehicleNotFound(): JSX.Element {
  const router = useRouter();
  const setVehicleNotFound = useSetAtom(vehicleNotFoundAtom);

  useEffect(() => {
    setVehicleNotFound(true);
    router.push('/listings');

    if (inIframe()) {
      window.parent.postMessage('not found', '*');
    }
  }, [router, setVehicleNotFound]);
  return <div />;
}

interface VehicleWithAdInfo {
  adInfo: AdInfo | undefined;
  vehicleWithExperience: VehicleWithExperience | undefined;
}

// get the vehicleWithExperience and adInfo data concurrently
async function getVehicleData(
  params: FetchVehicleWithExperienceParams
): Promise<VehicleWithAdInfo> {
  const vehicleAndAdInfo = await Promise.all([
    fetchVehicleWithExperience(params).catch((err) => {
      console.error(err);
      return err;
    }),
    fetchAdInfo(params.vin).catch(console.error),
  ]);
  return {
    adInfo: vehicleAndAdInfo[1] || undefined,
    vehicleWithExperience: vehicleAndAdInfo[0] || undefined,
  };
}

type VdpOrNotFoundGetServerSideProps = GetServerSideProps<VdpOrNotFoundProps, VdpOrNotFoundParams>;
export async function getServerSideProps({
  query,
  req,
  res,
  locale = i18next.language,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<VdpOrNotFoundGetServerSideProps>> {
  const ip = await getIpAddress(req);
  let reqLocation;
  if (ip) {
    const redisCachedMaxMindResult = await getIpMaxMindValue(ip);
    if (!redisCachedMaxMindResult) {
      reqLocation = await serverSideMaxMindGeolocate(ip);
      if (reqLocation) {
        await setIpMaxMindValue({ ip, value: reqLocation });
      }
    } else if (redisCachedMaxMindResult) {
      reqLocation = redisCachedMaxMindResult;
    }
  }

  const params = {
    latitude: reqLocation?.latitude,
    longitude: reqLocation?.longitude,
    locale,
    ...extractVehicleWithExperienceParamsFromQuery(query),
  };

  // Redirect users to listings page if valid coordinates are not provided
  const { vehicleWithExperience, adInfo } = await getVehicleData(params);
  if (
    vehicleWithExperience instanceof Error &&
    vehicleWithExperience.message === 'Valid coordinates were not provided'
  ) {
    return {
      redirect: {
        destination: '/listings',
        permanent: false,
      },
    };
  }
  if (!vehicleWithExperience?.vehicle?.active) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return { props: { vehicle: null } };
  }

  if (process.env.DISABLE_VDP_IFRAME_SECURITY === 'true') {
    res.removeHeader('x-frame-options');
  }
  res.setHeader('Cache-Control', 'public, max-age=900, must-revalidate');

  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    props: buildVdpPageProps(query, vehicleWithExperience, adInfo),
  };
}

export type VdpOrNotFoundProps = XOR<PresentVdpPageProps, { vehicle: null }>;

function VdpOrNotFound(props: VdpOrNotFoundProps): JSX.Element {
  const { t } = useTranslation();

  const { asPath } = useRouter();
  const isDirectToVdp = !asPath.includes('vin');

  function getVdpLayoutProps(): LayoutProps {
    return {
      title: props.vehicle
        ? `${props.vehicle.year} ${props.vehicle.make} ${props.vehicle.model}`
        : t(`main.meta.pageTitle`),
      description: '',
      includeAds: true,
      indexed: false,
      footer: <div />,
      showHeader: false,
      thumbnail: props.vehicle?.photo_urls[0],
    };
  }

  const layoutProps = getVdpLayoutProps();
  if (props?.vehicle && props?.experience) {
    return (
      <DirectToVdpContext.Provider value={{ isDirectToVdp }}>
        <Layout {...layoutProps}>
          {props.vehicle.active === false ? (
            <InactiveListing vehicle={props.vehicle} experience={props.experience} />
          ) : (
            <VdpPage {...props} />
          )}
        </Layout>
      </DirectToVdpContext.Provider>
    );
  }
  return VehicleNotFound();
}

export default VdpOrNotFound;
